import type { TableMetadata } from '../types/scriptProvider';

/** How one engine spells the `CREATE INDEX` statements a CREATE script carries. */
export type IndexStatementDialect = {
  /** Quote one identifier the way the engine does (`[x]`, `"x"`, `` `x` ``). */
  readonly quote: (name: string) => string;
  /** The quoted, qualified table the indexes are `ON`. */
  readonly table: string;
  /** Oracle: an index is a schema object of its own, so its name is
   *  `"OWNER"."IX"`; every other engine names it bare. */
  readonly indexSchema?: string;
  /** SQL Server: spell the catalog's `CLUSTERED` / `NONCLUSTERED` kind. Off
   *  elsewhere — `isClustered` is reported there too, but the keyword is not
   *  part of those engines' grammar. */
  readonly spellClustered?: boolean;
};

/** An index key that is index SYNTAX rather than a column name — an
 *  expression key (`lower(name)`, SQLite and Oracle report it as text) — goes
 *  out verbatim; quoting it would name a column that does not exist. */
function isExpressionKey(key: string): boolean {
  return key.includes('(');
}

/**
 * The `CREATE INDEX` statements a CREATE script spells after the table: one
 * per index the catalog reports in `TableMetadata.indexes`, in catalog order.
 *
 * The script is the Table Designer's ALTER-mode baseline, and the Designer's
 * Indexes tab lists the `CREATE INDEX` statements that follow the `CREATE
 * TABLE` — so a baseline without them showed every existing table as having
 * no indexes (user-reported, SQL Server, 2026-09-20). The same script backs
 * "Script table as → CREATE", which the docs already describe as including
 * the table's indexes.
 *
 * The primary key's index is not here: every engine's `tableIndexes` query
 * excludes it, and the CREATE TABLE spells the key as a constraint. An index
 * without key columns (a SQL Server clustered columnstore) or without a name
 * (SQL Server's heap row) is skipped — neither has a `CREATE INDEX` form that
 * this statement could spell.
 */
export function createIndexStatements(metadata: TableMetadata, dialect: IndexStatementDialect): string[] {
  const statements: string[] = [];
  for (const index of metadata.indexes ?? []) {
    if (!index.name || index.columns.length === 0) continue;
    const unique = index.isUnique ? 'UNIQUE ' : '';
    const kind = dialect.spellClustered && index.isClustered !== undefined
      ? (index.isClustered ? 'CLUSTERED ' : 'NONCLUSTERED ')
      : '';
    const name = dialect.indexSchema === undefined
      ? dialect.quote(index.name)
      : `${dialect.quote(dialect.indexSchema)}.${dialect.quote(index.name)}`;
    const keys = index.columns.map(key => (isExpressionKey(key) ? key : dialect.quote(key))).join(', ');
    const include = index.includedColumns?.length
      ? ` INCLUDE (${index.includedColumns.map(dialect.quote).join(', ')})`
      : '';
    const where = index.filterDefinition ? ` WHERE ${index.filterDefinition}` : '';
    statements.push(`CREATE ${unique}${kind}INDEX ${name} ON ${dialect.table} (${keys})${include}${where};`);
  }
  return statements;
}

/** `script` (the CREATE TABLE) followed, after a blank line, by the table's
 *  `CREATE INDEX` statements — or `script` unchanged when there are none. */
export function appendIndexStatements(script: string, metadata: TableMetadata, dialect: IndexStatementDialect): string {
  const statements = createIndexStatements(metadata, dialect);
  if (statements.length === 0) return script;
  return `${script.trimEnd()}\n\n${statements.join('\n')}`;
}
